import { forwardRef, type ReactNode, type ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/utils'

/* ── SettingsCard ────────────────────────────────────────────── */

interface SettingsCardProps {
  children: ReactNode
  className?: string
}

export function SettingsCard({ children, className }: SettingsCardProps) {
  return (
    <div
      className={cn(
        'rounded-lg border border-border bg-card overflow-hidden divide-y divide-border',
        className,
      )}
    >
      {children}
    </div>
  )
}

/* ── SettingsRow ─────────────────────────────────────────────── */

interface SettingsRowProps {
  icon?: ReactNode
  label: ReactNode
  description?: ReactNode
  trailing?: ReactNode
  onClick?: () => void
  destructive?: boolean
  disabled?: boolean
  className?: string
  /** Extra props forwarded to the underlying button (e.g. data-testid). */
  buttonProps?: Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'onClick' | 'disabled' | 'className'
  > & { [key: `data-${string}`]: string | undefined }
}

// forwardRef so the row can act as a Radix `asChild` trigger (RegionPicker).
export const SettingsRow = forwardRef<HTMLButtonElement, SettingsRowProps>(function SettingsRow(
  { icon, label, description, trailing, onClick, destructive, disabled, className, buttonProps },
  ref,
) {
  const content = (
    <>
      {icon && (
        <span className={cn('shrink-0', destructive ? 'text-destructive' : 'text-muted-foreground')}>
          {icon}
        </span>
      )}
      <span className="flex-1 min-w-0 text-left">
        <span
          className={cn(
            'text-sm block',
            destructive ? 'text-destructive' : 'text-foreground',
          )}
        >
          {label}
        </span>
        {description && (
          <span className="text-xs text-muted-foreground block">
            {description}
          </span>
        )}
      </span>
      {trailing && <span className="shrink-0 ml-auto">{trailing}</span>}
    </>
  )

  const shared = cn(
    'w-full flex items-center gap-3 px-4 py-3 text-sm',
    onClick && 'cursor-pointer transition-colors hover:bg-muted/50',
    disabled && 'opacity-60',
    className,
  )

  if (onClick) {
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={shared}
        {...buttonProps}
      >
        {content}
      </button>
    )
  }

  return <div className={shared}>{content}</div>
})
